import React, { useState, useEffect } from "react";
import {
  Row, Col, Card, message, Divider, Tag, Empty, Space, Button, Tooltip, Typography,
  Modal, Form, Input, InputNumber, Descriptions,
  Checkbox,
  Select,
} from "antd";
import { UserOutlined, PlusOutlined, CheckOutlined, DeleteOutlined, SolutionOutlined } from "@ant-design/icons";
import { HeaderPage } from "../../../components/headerPage";
import { HeaderTitle } from "../../../components/headerTitle";
import { useNavigate } from "react-router-dom";
import http from "../../../services/http";
import dayjs from "dayjs";
import { TableCustom } from "../../../components/table";
import { maskCPF, validarCPF, parseToInt, randomInt } from "../../../helpers/masks";
const { Text } = Typography;

export const CampanhasVacinacaoLancamento = ({ to }) => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [campanhas, setCampanhas] = useState([]);
  const [firstNumber] = useState(randomInt(9));
  const [secondNumber] = useState(randomInt(9));

  const [showModal, setShowModal] = useState(false);
  const [registroSelecionado, setRegistroSelecionado] = useState(null);

  useEffect(() => {
    carregarCampanhasUsuario();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // CARREGAR CAMPANHAS DO USUÁRIO
  const carregarCampanhasUsuario = async () => {
    setLoading(true);
    const api = await http("get", `v1/campanhas_vacinacao_lancamento/campanhas-usuario`, []);
    if (api.statusCode === 200) {
      setCampanhas(api?.response?.data ?? []);
    } else {
      message.error(api.response.message);
    }
    setLoading(false);
  };

  // ABRIR MODAL
  const abrirModal = (registro) => {
    setRegistroSelecionado(registro);
    form.resetFields();
    form.setFieldsValue({ dependentes: [] });
    setShowModal(true);
  };

  // FECHAR MODAL
  const fecharModal = () => {
    setShowModal(false);
    setRegistroSelecionado(null);
    form.resetFields();
  };

  const confirmarParticipacao = async () => {
    try {
      const formulario = await form.validateFields();

      if (firstNumber + secondNumber !== parseToInt(formulario?.operacao_matematica)) {
        form.setFields([
          {
            name: "operacao_matematica",
            errors: ["Resultado incorreto!"],
          },
        ]);
        return;
      }

      setLoading(true);

      const payload = {
        id: registroSelecionado.key,
        operacao_matematica: `${firstNumber} + ${secondNumber} = ${parseToInt(formulario?.operacao_matematica)}`,
        autoriza_desconto_folha: formulario.autoriza_desconto_folha,
        qtd_parcelas: formulario.autoriza_desconto_folha ? formulario.qtd_parcelas : null,
        dependentes: formulario.dependentes ?? [],
      };

      const api = await http("post", `v1/campanhas_vacinacao_lancamento/confirmar-participacao`, payload);
      if (api.statusCode === 200) {
        message.success("Participação confirmada com sucesso!");
        fecharModal();
        carregarCampanhasUsuario();
      } else {
        message.error(api.response.message);
      }
    } catch (err) {
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      title: "Campanha",
      dataIndex: "titulo_campanha",
    },
    {
      title: "Ano",
      dataIndex: "ano",
    },
    {
      title: "Período",
      dataIndex: "data_inicio",
      render: (_, e) =>
        `${dayjs(e.data_inicio).format("DD/MM/YYYY")} até ${dayjs(e.data_fim).format("DD/MM/YYYY")}`,
    },
    {
      title: "Situação",
      dataIndex: "status_lancamento",
      render: (_, e) => {
        const hoje = dayjs();
        const foraDoPrazo = dayjs(e.data_fim).isBefore(hoje, "day");

        if (e.status_campanha === 0 && e.status_lancamento === 1) {
          return <Tag color="green">Participação confirmada - Finalizado</Tag>;
        }

        if (e.status_campanha === 0) {
          return <Tag color="red">Campanha encerrada</Tag>;
        }

        if (foraDoPrazo) {
          return <Tag color="red">Fora do prazo</Tag>;
        }

        const statusConfig = {
          1: { label: "Participação confirmada", color: "green" },
          2: { label: "Pendente", color: "orange" },
        };

        const cfg = statusConfig[e.status_lancamento];
        return cfg ? <Tag color={cfg.color}>{cfg.label}</Tag> : "-";
      }
    },
    {
      title: "Ações",
      dataIndex: "acoes",
      render: (_, e) => {
        const hoje = dayjs();
        const foraDoPrazo = dayjs(e.data_fim).isBefore(hoje, "day");
        const podeResponder = !foraDoPrazo && e.status_lancamento === 2 && e.status_campanha !== 0;

        if (!podeResponder) {
          return "-";
        }

        return (
          <Space.Compact>
            <Tooltip title="Participar da campanha">
              <Button onClick={() => abrirModal(e)} icon={<SolutionOutlined />}>
                Confirmar participação
              </Button>
            </Tooltip>
          </Space.Compact>
        );
      },
    },
  ];

  const modalResposta = (
    <Modal
      title={<Text style={{ fontSize: 20 }}>Detalhes da Campanha</Text>}
      open={showModal}
      onCancel={fecharModal}
      width={800}
      footer={[
        <Button key="confirmar" type="primary" icon={<CheckOutlined />} loading={loading} onClick={() => confirmarParticipacao()}>
          Confirmar participação
        </Button>
      ]}>
      <Descriptions column={2} size="small" bordered style={{ marginBottom: 20 }}>
        <Descriptions.Item label="Campanha" span={2}>
          {registroSelecionado?.titulo_campanha}
        </Descriptions.Item>
        <Descriptions.Item label="Período" span={2}>
          {dayjs(registroSelecionado?.data_inicio).format("DD/MM/YYYY")} até{" "}
          {dayjs(registroSelecionado?.data_fim).format("DD/MM/YYYY")}
        </Descriptions.Item>
        <Descriptions.Item label="Detalhes" span={2}>
          {registroSelecionado?.observacoes_campanha || "Sem observações"}
        </Descriptions.Item>
      </Descriptions>

      <Divider orientation="left" orientationMargin={0}>
        Autorização de Desconto
      </Divider>

      <Form form={form} layout="vertical" autoComplete="off">
        <Row gutter={12} align="middle">
          <Col span={24}>
            <Form.Item
              name="autoriza_desconto_folha"
              valuePropName="checked"
              rules={[
                {
                  validator: (_, value) =>
                    value ? Promise.resolve() : Promise.reject(new Error("É necessário autorizar o desconto em folha!")),
                },
              ]}>
              <Checkbox>Autorizo desconto em folha</Checkbox>
            </Form.Item>
          </Col>

          <Col span={24}>
            <Form.Item
              noStyle
              shouldUpdate={(prev, curr) => prev.autoriza_desconto_folha !== curr.autoriza_desconto_folha}>
              {({ getFieldValue }) =>
                getFieldValue("autoriza_desconto_folha") ? (
                  <Form.Item
                    name="qtd_parcelas"
                    label="Quantidade de parcelas"
                    rules={[{ required: true, message: "Selecione a quantidade de parcelas!" }]}>
                    <Select
                      placeholder="Selecione"
                      options={[
                        { value: 1, label: "1x" },
                        { value: 2, label: "2x" },
                      ]}
                    />
                  </Form.Item>
                ) : null
              }
            </Form.Item>
          </Col>
        </Row>
      </Form>

      <Divider orientation="left" orientationMargin={0}>
        Dependentes
      </Divider>
      
      <Form form={form} layout="vertical" autoComplete="off">
        <Form.List name="dependentes">
          {(fields, { add, remove }) => (
            <>
              {fields.map(({ key, name, ...restField }) => (
                <Row gutter={12} key={key} align="middle">
                  <Col span={10}>
                    <Form.Item
                      {...restField}
                      name={[name, "nome"]}
                      rules={[{ required: true, message: "Informe o nome do dependente!" }]}>
                      <Input placeholder="Nome completo" />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item
                      {...restField}
                      name={[name, "cpf"]}
                      rules={[
                        { required: true, message: "Informe o CPF do dependente!" },
                        {
                          validator: (_, value) => {
                            if (!value) return Promise.resolve();
                            const cpfLimpo = value.replace(/\D/g, "");

                            if (cpfLimpo.length !== 11) {
                              return Promise.reject(new Error("CPF incompleto!"));
                            }
                            if (!validarCPF(cpfLimpo)) {
                              return Promise.reject(new Error("CPF inválido!"));
                            }
                            return Promise.resolve();
                          },
                        },
                      ]}>
                      <Input
                        placeholder="CPF"
                        maxLength={14}
                        onChange={(ev) => {
                          const valorFormatado = maskCPF(ev.target.value);
                          form.setFieldValue(["dependentes", name, "cpf"], valorFormatado);
                        }}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={4}>
                    <Form.Item
                      {...restField}
                      name={[name, "idade"]}
                      rules={[{ required: true, message: "Informe a idade do dependente!" }]}>
                      <InputNumber placeholder="Idade" min={0} max={120} style={{ width: "100%" }} />
                    </Form.Item>
                  </Col>
                  <Col span={2} style={{ display: "flex", justifyContent: "center" }}>
                    <Form.Item>
                      <Tooltip title="Remover dependente">
                        <Button
                          danger
                          type="text"
                          shape="circle"
                          icon={<DeleteOutlined />}
                          onClick={() => remove(name)}
                        />
                      </Tooltip>
                    </Form.Item>
                  </Col>
                </Row>
              ))}
              <Form.Item>
                <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                  Adicionar dependente
                </Button>
              </Form.Item>
            </>
          )}
        </Form.List>
      </Form>

      <Divider orientation="left" orientationMargin={0}>
        Confirmação
      </Divider>

      <Form form={form} layout="vertical" autoComplete="off">
        <Form.Item
          label="Resolva a operação matemática:"
          required
          style={{ marginBottom: 0 }}>
          <Form.Item
            name="operacao_matematica"
            rules={[{ required: true, message: "Resolva a operação matemática!" }]}
            noStyle>
            <Row align="middle" gutter={8}>
              <Col>
                <Text style={{ fontSize: 20 }}>
                  {firstNumber} + {secondNumber} =
                </Text>
              </Col>
              <Col>
                <InputNumber
                  controls={false}
                  autoComplete="off"
                  className="input-number"
                  precision={0}
                  min={0}
                  style={{ width: 100 }}
                  size="large"
                  disabled={loading}
                  placeholder="..."
                />
              </Col>
            </Row>
          </Form.Item>
        </Form.Item>
      </Form>
      <Divider />
    </Modal>
  );

  return (
    <Row align="middle" justify="center" className="content-row">
      {modalResposta}
      <Col span={24} align="middle" justify="center">
        <Col xs={24} sm={24} md={22} lg={22} xl={20} xxl={16} align="left">
          <div className="content-item">
            <HeaderPage title="Campanhas de Vacinação" onClickBack={() => navigate(to)} />
            <HeaderTitle
              title={["Campanha de Vacinação", "Usuário"]}
              subtitle="Campanhas de Vacinação - Lista"
              icon={<UserOutlined />}
              loading={loading}
            />
            <Divider />
            <Card bordered={false} bodyStyle={{ padding: "3%" }}>
              {campanhas.length > 0 ? (
                <TableCustom loading={loading} columns={columns} dataSource={campanhas} rowKey="key"/>
              ) : (
                <Empty description="Nenhuma campanha pendente!" />
              )}
            </Card>
          </div>
        </Col>
      </Col>
    </Row>
  );
};